import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Marquee from 'react-fast-marquee';
import CountUp from 'react-countup';
import { PublicLayout } from '../components/Layout';
import { SEO } from '../components/SEO';
import { Button } from '../components/ui/button';
import {
  ArrowRight,
  ArrowUpRight,
  Briefcase,
  CheckCircle2,
  Clock4,
  Code2,
  Compass,
  Layers,
  ShieldCheck,
  Sparkles,
  Users2,
} from 'lucide-react';

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0 },
};

const clientLogos = [
  { name: 'Helptech', src: 'https://helptechpc.com.br/wp-content/uploads/2024/04/logo-azul-mini-02.png' },
  { name: 'BNP Soluções', src: 'https://bnpsolucoes.com.br/wp-content/uploads/2023/08/logo_black.png' },
  { name: 'AxxisPay', src: 'https://customer-assets.emergentagent.com/job_fd402689-9cfa-4f7b-b978-795dd20cf5c5/artifacts/fmeratv3_AxxisPay.png', wide: true },
  { name: 'Fazenda Mariano', src: 'https://customer-assets.emergentagent.com/job_61b748f5-4cc4-47b0-9f16-c1b94d0ddaf0/artifacts/ukov728h_logofmarianosimbolo300x271.jpg' },
  { name: 'VRX', src: 'https://customer-assets.emergentagent.com/job_61b748f5-4cc4-47b0-9f16-c1b94d0ddaf0/artifacts/b1sqyxka_vrx%20logo.jpeg' },
  { name: 'Portal Compras Públicas', src: 'https://customer-assets.emergentagent.com/job_61b748f5-4cc4-47b0-9f16-c1b94d0ddaf0/artifacts/s60lg5yg_portal%20de%20compras%20publicas.png' },
  { name: 'SocialLab', src: 'https://customer-assets.emergentagent.com/job_fd402689-9cfa-4f7b-b978-795dd20cf5c5/artifacts/o6xm105g_socialab.png' },
];

const pillars = [
  {
    icon: Code2,
    title: 'Recrutadores que já foram devs',
    desc: 'Quem entrevista entende de stack, leu Pull Request e brigou com produção. Match técnico verdadeiro, não palavra-chave em currículo.',
  },
  {
    icon: Compass,
    title: 'Metodologia Torke®',
    desc: 'Processo proprietário em camadas: contexto do negócio, sondagem técnica real, leitura cultural e validação contínua durante a alocação.',
  },
  {
    icon: Layers,
    title: 'Squads sob medida',
    desc: 'Devs, designers, arquitetos e gestores compostos pelo perfil do seu projeto — não um pool genérico esperando alocação.',
  },
  {
    icon: ShieldCheck,
    title: 'Acompanhamento ativo',
    desc: 'Gestão de pessoas e entregas durante todo o contrato. Você nunca fica sozinho com um profissional alocado.',
  },
];

const torkeSteps = [
  {
    num: '01',
    title: 'Contexto antes de currículo',
    desc: 'Entendemos o negócio, o time atual, a maturidade técnica e os objetivos. Só depois abrimos a vaga.',
  },
  {
    num: '02',
    title: 'Sondagem técnica real',
    desc: 'Quem entrevista é dev. Conversa de código, decisão de arquitetura, experiência com produção. Sem teste genérico.',
  },
  {
    num: '03',
    title: 'Leitura cultural',
    desc: 'Avaliamos soft skills, postura em ambiente remoto e fit com a cultura do cliente — não só do mercado.',
  },
  {
    num: '04',
    title: 'Acompanhamento contínuo',
    desc: 'Durante a alocação, fazemos follow-up estruturado com cliente e profissional. Resultado percebido, não suposto.',
  },
];

const stats = [
  { num: 30, suffix: '+', label: 'Clientes atendidos' },
  { num: 100, suffix: '+', label: 'Talentos contratados' },
  { num: 3.8, suffix: 'M', prefix: 'R$ ', decimals: 1, label: 'Em valor gerado' },
  { num: 15, suffix: ' dias', label: 'SLA médio para alocar' },
];

const Landing = () => {
  const primary = { color: 'var(--text-primary)' };
  const muted = { color: 'var(--text-muted)' };
  const faint = { color: 'var(--text-faint)' };

  return (
    <PublicLayout>
      <SEO
        title="Squad as a Service por Arcano Projetos"
        description="Aceleramos sua entrega com squads de alta performance. Metodologia Torke®: recrutadores que já foram desenvolvedores. +30 clientes, +100 talentos, R$ 3,8M em valor gerado."
        path="/"
        jsonLd={[
          {
            '@context': 'https://schema.org',
            '@type': 'Organization',
            name: 'Rent a Squad',
            alternateName: 'Rent a Squad by Arcano Projetos',
            url: 'https://rentasquad.co',
            logo: 'https://rentasquad.co/images/logo-azul.png',
            description: 'Squad as a Service entregue pela Arcano Projetos. Metodologia Torke® para alocação de squads de tecnologia.',
            parentOrganization: {
              '@type': 'Organization',
              name: 'Arcano Projetos de Tecnologia',
              url: 'https://arcanoprojetos.com',
            },
            contactPoint: [
              {
                '@type': 'ContactPoint',
                telephone: '+55-35-99174-3960',
                contactType: 'sales',
                email: 'alex.philippe@arcanoprojetos.com',
                availableLanguage: ['Portuguese'],
                areaServed: 'BR',
              },
            ],
          },
          {
            '@context': 'https://schema.org',
            '@type': 'WebSite',
            name: 'Rent a Squad',
            url: 'https://rentasquad.co',
            inLanguage: 'pt-BR',
            publisher: { '@type': 'Organization', name: 'Arcano Projetos de Tecnologia' },
            potentialAction: {
              '@type': 'SearchAction',
              target: 'https://rentasquad.co/vagas?q={search_term_string}',
              'query-input': 'required name=search_term_string',
            },
          },
        ]}
      />
      {/* HERO */}
      <section className="relative overflow-hidden">
        {/* Background image */}
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1543269865-cbf427effbad?auto=format&fit=crop&w=1600&q=70"
            srcSet="https://images.unsplash.com/photo-1543269865-cbf427effbad?auto=format&fit=crop&w=800&q=70 800w, https://images.unsplash.com/photo-1543269865-cbf427effbad?auto=format&fit=crop&w=1200&q=70 1200w, https://images.unsplash.com/photo-1543269865-cbf427effbad?auto=format&fit=crop&w=1600&q=70 1600w, https://images.unsplash.com/photo-1543269865-cbf427effbad?auto=format&fit=crop&w=2000&q=70 2000w"
            sizes="100vw"
            alt="Equipe diversa de pessoas colaborando em ambiente de trabalho"
            loading="eager"
            fetchPriority="high"
            decoding="async"
            width="1600"
            height="1067"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 hero-bg-overlay" />
        </div>
        <div className="absolute inset-0 bg-grid opacity-30 pointer-events-none z-[1]" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 sm:pt-28 pb-24 sm:pb-32 relative z-10">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeUp}
            transition={{ duration: 0.6 }}
            className="max-w-4xl"
          >
            <div
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold mb-8"
              style={{
                background: 'rgba(33,136,255,0.08)',
                border: '1px solid rgba(33,136,255,0.25)',
                color: 'var(--accent-primary)',
                letterSpacing: '0.08em',
              }}
            >
              <Sparkles className="w-3.5 h-3.5" />
              UM PRODUTO ARCANO PROJETOS
            </div>

            <h1
              className="text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tighter leading-[1.02] mb-8"
              style={primary}
            >
              Aceleramos sua
              <br />
              entrega com squads
              <br />
              de <span style={{ color: 'var(--accent-primary)' }}>alta performance.</span>
            </h1>

            <p className="text-lg sm:text-xl max-w-2xl mb-10 leading-relaxed" style={muted}>
              O Rent a Squad empresta a experiência das equipes da Arcano para que sua empresa
              foque no estratégico — e deixe a execução técnica em mãos que já passaram por ela.
            </p>

            <div className="flex flex-wrap gap-3">
              <Button
                data-testid="hero-cta-contact"
                onClick={() =>
                  document.getElementById('contato')?.scrollIntoView({ behavior: 'smooth' })
                }
                className="btn-primary text-white px-7 h-12 rounded-md text-base font-semibold"
              >
                Falar com especialista
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CLIENTS MARQUEE */}
      <section className="py-16 border-y" style={{ borderColor: 'var(--border-secondary)' }}>
        <p
          className="text-center text-xs font-semibold mb-10 tracking-[0.2em] uppercase"
          style={faint}
        >
          Empresas que confiam na Arcano
        </p>
        <Marquee gradient={false} speed={35} pauseOnHover>
          {clientLogos.map((logo, i) => (
            <div
              key={`${logo.name}-${i}`}
              className="mx-12 sm:mx-16 flex items-center justify-center h-20 sm:h-24 opacity-70 hover:opacity-100 transition-opacity"
              title={logo.name}
            >
              <img
                src={logo.src}
                alt={logo.name}
                loading="lazy"
                className={`w-auto object-contain ${logo.wide ? 'max-h-10 sm:max-h-12' : 'max-h-20 sm:max-h-24'}`}
                style={{ filter: 'grayscale(60%)' }}
              />
            </div>
          ))}
        </Marquee>
      </section>

      {/* PROBLEM vs SOLUTION (Torke) */}
      <section className="py-24 sm:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-100px' }}
            transition={{ duration: 0.7 }}
            className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter leading-[1.05] max-w-5xl"
            style={primary}
          >
            Recrute com quem já foi desenvolvedor,
            <br />
            <span style={{ color: 'var(--accent-primary)' }}>
              recrute com a metodologia Torke®.
            </span>
          </motion.h2>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 mt-20">
            {/* Pains */}
            <div>
              <p
                className="text-xs font-semibold tracking-[0.2em] uppercase mb-6"
                style={{ color: 'var(--error)' }}
              >
                O que sempre dá errado
              </p>
              <ul className="space-y-5">
                {[
                  'CVs sem contexto técnico real',
                  'Recrutadores que não sabem ler o stack',
                  'Match cultural inexistente',
                  'Profissional alocado e esquecido no cliente',
                ].map((item) => (
                  <li
                    key={item}
                    className="flex items-start gap-3 text-base sm:text-lg leading-relaxed"
                    style={muted}
                  >
                    <span
                      className="w-1.5 h-1.5 rounded-full mt-3 flex-shrink-0"
                      style={{ background: 'var(--error)' }}
                    />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Solution */}
            <div>
              <p
                className="text-xs font-semibold tracking-[0.2em] uppercase mb-6"
                style={{ color: 'var(--accent-primary)' }}
              >
                Como a Torke® resolve
              </p>
              <ul className="space-y-5">
                {[
                  'Entendemos seu negócio antes de abrir vaga',
                  'Sondagem técnica conduzida por quem já foi dev',
                  'Avaliação cultural e fit no time real',
                  'Acompanhamento ativo durante toda a alocação',
                ].map((item) => (
                  <li
                    key={item}
                    className="flex items-start gap-3 text-base sm:text-lg leading-relaxed"
                    style={primary}
                  >
                    <CheckCircle2
                      className="w-5 h-5 mt-1 flex-shrink-0"
                      style={{ color: 'var(--accent-primary)' }}
                    />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* STATS */}
      <section
        className="py-20 sm:py-24"
        style={{ background: 'var(--bg-secondary)', borderTop: '1px solid var(--border-secondary)', borderBottom: '1px solid var(--border-secondary)' }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p
            className="text-xs font-semibold tracking-[0.2em] uppercase mb-12 text-center"
            style={faint}
          >
            Os números mostram os resultados
          </p>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-12 lg:gap-x-12 xl:gap-x-16">
            {stats.map((s) => (
              <motion.div
                key={s.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
                className="text-center lg:text-left"
                data-testid={`stat-${s.label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <p
                  className="text-4xl sm:text-5xl lg:text-5xl xl:text-6xl font-bold tracking-tighter mb-3 whitespace-nowrap"
                  style={primary}
                >
                  <CountUp
                    end={s.num}
                    duration={2.4}
                    decimals={s.decimals || 0}
                    prefix={s.prefix || ''}
                    suffix={s.suffix || ''}
                    enableScrollSpy
                    scrollSpyOnce
                  />
                </p>
                <p className="text-sm sm:text-base" style={muted}>
                  {s.label}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* PILLARS */}
      <section className="py-24 sm:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mb-16">
            <p
              className="text-xs font-semibold tracking-[0.2em] uppercase mb-4"
              style={{ color: 'var(--accent-primary)' }}
            >
              Como entregamos
            </p>
            <h2
              className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-[1.1]"
              style={primary}
            >
              Quatro pilares que sustentam cada squad da Rent a Squad.
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {pillars.map((p, i) => {
              const Icon = p.icon;
              return (
                <motion.div
                  key={p.title}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.08 }}
                  className="rounded-xl p-8 hover:-translate-y-1 transition-all duration-300"
                  style={{
                    background: 'var(--bg-card)',
                    border: '1px solid var(--border-primary)',
                  }}
                  data-testid={`pillar-${i}`}
                >
                  <div
                    className="w-11 h-11 rounded-lg flex items-center justify-center mb-6"
                    style={{ background: 'rgba(33,136,255,0.1)' }}
                  >
                    <Icon className="w-5 h-5" style={{ color: 'var(--accent-primary)' }} />
                  </div>
                  <h3
                    className="text-xl sm:text-2xl font-semibold tracking-tight mb-3"
                    style={primary}
                  >
                    {p.title}
                  </h3>
                  <p className="leading-relaxed" style={muted}>
                    {p.desc}
                  </p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* HOW TORKE WORKS - Steps */}
      <section
        className="py-24 sm:py-32"
        style={{
          background: 'var(--bg-secondary)',
          borderTop: '1px solid var(--border-secondary)',
          borderBottom: '1px solid var(--border-secondary)',
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mb-16">
            <p
              className="text-xs font-semibold tracking-[0.2em] uppercase mb-4"
              style={{ color: 'var(--accent-primary)' }}
            >
              Metodologia Torke®
            </p>
            <h2
              className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-[1.1]"
              style={primary}
            >
              Quatro etapas que tornam o match real.
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {torkeSteps.map((s, i) => (
              <motion.div
                key={s.num}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                className="rounded-xl p-8"
                style={{
                  background: 'var(--bg-card)',
                  border: '1px solid var(--border-primary)',
                }}
                data-testid={`torke-step-${s.num}`}
              >
                <p
                  className="text-5xl font-bold tracking-tighter mb-6"
                  style={{ color: 'var(--accent-primary)', opacity: 0.9 }}
                >
                  {s.num}
                </p>
                <h3
                  className="text-lg sm:text-xl font-semibold tracking-tight mb-3"
                  style={primary}
                >
                  {s.title}
                </h3>
                <p className="text-sm sm:text-base leading-relaxed" style={muted}>
                  {s.desc}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* JOBS BANNER */}
      <section className="py-20 sm:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div
            className="rounded-2xl p-10 sm:p-14 lg:p-20 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8"
            style={{
              background: 'var(--bg-secondary)',
              border: '1px solid var(--border-primary)',
            }}
          >
            <div className="max-w-2xl">
              <p
                className="text-xs font-semibold tracking-[0.2em] uppercase mb-4"
                style={{ color: 'var(--accent-primary)' }}
              >
                É desenvolvedor(a)?
              </p>
              <h2
                className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-[1.1] mb-6"
                style={primary}
              >
                Faça parte de squads que entregam de verdade.
              </h2>
              <p className="text-base sm:text-lg leading-relaxed" style={muted}>
                Vagas em projetos reais, com clientes reais e gestão de pessoas próxima.
                Sem alocação às cegas.
              </p>
            </div>
            <Link
              to="/vagas"
              data-testid="jobs-banner-cta"
              className="inline-flex items-center gap-2 text-base font-semibold whitespace-nowrap group"
              style={{ color: 'var(--accent-primary)' }}
            >
              Ver vagas abertas
              <ArrowUpRight className="w-5 h-5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </div>
        </div>
      </section>

      {/* CONTACT CTA */}
      <section
        id="contato"
        className="py-24 sm:py-32"
        style={{ borderTop: '1px solid var(--border-secondary)' }}
      >
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p
            className="text-xs font-semibold tracking-[0.2em] uppercase mb-4"
            style={{ color: 'var(--accent-primary)' }}
          >
            Pronto para acelerar seu time?
          </p>
          <h2
            className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter leading-[1.05] mb-10"
            style={primary}
          >
            Vamos montar seu próximo squad.
          </h2>
          <div className="flex flex-wrap justify-center gap-3 mb-12">
            <a
              href="https://wa.me/5535991743960"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-whatsapp"
            >
              <Button className="btn-primary text-white px-7 h-12 rounded-md text-base font-semibold">
                Falar no WhatsApp
                <ArrowUpRight className="w-4 h-4 ml-2" />
              </Button>
            </a>
            <a
              href="mailto:alex.philippe@arcanoprojetos.com"
              data-testid="contact-email"
            >
              <Button
                variant="outline"
                className="h-12 px-7 rounded-md text-base font-semibold"
                style={{
                  borderColor: 'var(--border-primary)',
                  color: 'var(--text-primary)',
                  background: 'transparent',
                }}
              >
                Enviar e-mail
              </Button>
            </a>
          </div>
          <div
            className="flex flex-wrap justify-center gap-x-10 gap-y-3 text-sm"
            style={muted}
          >
            <span className="flex items-center gap-2">
              <Clock4 className="w-4 h-4" />
              Seg–Sex 9h às 18h · Sáb 9h às 13h
            </span>
            <span className="flex items-center gap-2">
              <Users2 className="w-4 h-4" />
              +35 99174.3960
            </span>
            <span className="flex items-center gap-2">
              <Briefcase className="w-4 h-4" />
              alex.philippe@arcanoprojetos.com
            </span>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
};

export default Landing;
